import { Paragraph, Radio } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { ILocation } from "@/data/locations";
import { TUserRole } from "@/data/session";
import { Flex, Group, Label } from "@/eworkitComponents";
import { FormikForm, FormikTextInput } from "@/formikComponents";
import { FormikTypeaheadLocationHints } from "@/views/components/Formik";
import {
  EMAIL_MAX_CHARACTERS,
  NAME_MAX_CHARACTERS,
  POSITION_MAX_CHARACTERS,
} from "@/views/ConsultantProfiles/Detail/GeneralSection/GeneralSectionEdit";

export interface IInvitationFormValues {
  firstName: string;
  lastName: string;
  email: string;
  position: { name: string };
  location: ILocation | null;
  skipInvitation: boolean;
}

const FORM_INITIAL_VALUES: IInvitationFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  position: { name: "" },
  location: null,
  skipInvitation: false,
};

const formValidationSchema = Yup.object().shape({
  firstName: Yup.string().max(NAME_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH).required(VALIDATION_TYPES.REQUIRED),
  lastName: Yup.string().max(NAME_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH).required(VALIDATION_TYPES.REQUIRED),
  email: Yup.string()
    .max(EMAIL_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH)
    .required(VALIDATION_TYPES.REQUIRED)
    .matches(/^[a-zA-Z\d._-]+@[a-zA-Z\d][a-zA-Z\d-.]{0,61}[a-zA-Z\d]\.[a-zA-Z]{2,}$/, VALIDATION_TYPES.EMAIL),
  location: Yup.object().nullable(true).required(VALIDATION_TYPES.REQUIRED),
  position: Yup.object().shape({
    name: Yup.string().max(POSITION_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH),
  }),
});

interface IInvitationFormProps {
  form: FormikProps<IInvitationFormValues>;
  onCancel: () => void;
  selectedRole?: TUserRole | null;
}

export const InvitationForm: FC<IInvitationFormProps> = ({ form, onCancel, selectedRole = null }) => {
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const { t } = useTranslation("session");

  useEffect(() => {
    form.setFieldValue("skipInvitation", !selectedRole || selectedRole === "CONSULTANT");
  }, [selectedRole]);

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md">
        <Flex innerMargin="xl" direction={isSmallScreen ? "column" : "row"} align="flex-start">
          <Group
            direction="column"
            required
            label={t("CONSULTANT_PROFILES.INVITATION.FORM.FIRST_NAME")}
            width={{ xxs: 12, md: 6 }}>
            <FormikTextInput type="text" name="firstName" value={form.values.firstName} required size="lg" />
            <FormikError
              name="firstName"
              overrideMessages={{
                MAX_LENGTH: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                  value: NAME_MAX_CHARACTERS,
                }),
              }}
            />
          </Group>
          <Group
            direction="column"
            required
            label={t("CONSULTANT_PROFILES.INVITATION.FORM.LAST_NAME")}
            width={{ xxs: 12, md: 6 }}>
            <FormikTextInput type="text" name="lastName" value={form.values.lastName} required size="lg" />
            <FormikError
              name="lastName"
              overrideMessages={{
                MAX_LENGTH: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                  value: NAME_MAX_CHARACTERS,
                }),
              }}
            />
          </Group>
        </Flex>

        <Group direction="column" label={t("CONSULTANT_PROFILES.INVITATION.FORM.EMAIL")} width={12} required>
          <FormikTextInput name="email" value={form.values.email} required size="lg" />
          <FormikError
            name="email"
            overrideMessages={{
              ALREADY_EXISTS: t("COMPANY.DETAIL.LIST.ADD.ERROR_MESSAGES.ALREADY_EXISTS"),
              MAX_LENGTH: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                value: NAME_MAX_CHARACTERS,
              }),
            }}
          />
        </Group>

        <Flex innerMargin="xl" direction={"column"}>
          <Group direction="column" label={t("CONSULTANT_PROFILES.INVITATION.FORM.LOCATION")} width={12} required>
            <FormikTypeaheadLocationHints
              name="location"
              value={form.values.location}
              size="lg"
              valueKey="name"
              labelKey="name"
              placeholder=""
              required
            />
            <FormikError name="location" />
          </Group>
        </Flex>

        <Group direction="column" label={t("CONSULTANT_PROFILES.INVITATION.FORM.COMPANY_POSITION")} width={12}>
          <FormikTextInput
            placeholder={t("CONSULTANT_PROFILES.DETAIL.COMPANY_POSITION.PLACEHOLDER")}
            type="text"
            name="position.name"
            value={form.values.position.name}
            size="lg"
          />
          <FormikError
            name="position.name"
            overrideMessages={{
              [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                value: POSITION_MAX_CHARACTERS,
              }),
            }}
          />
        </Group>

        <Flex direction={"column"}>
          <Label required>{t("CONSULTANT_PROFILES.INVITATION.FORM.SEND_INVITATION")}</Label>
          <Flex align={"center"} innerMargin={"xs"}>
            <Radio checked={!form.values.skipInvitation} onChange={() => form.setFieldValue("skipInvitation", false)} />
            <Paragraph>{t("CONSULTANT_PROFILES.INVITATION.FORM.RADIO_YES")}</Paragraph>
          </Flex>
          <Flex align={"center"} innerMargin={"xs"}>
            <Radio checked={form.values.skipInvitation} onChange={() => form.setFieldValue("skipInvitation", true)} />
            <Paragraph>{t("CONSULTANT_PROFILES.INVITATION.FORM.RADIO_NO")}</Paragraph>
          </Flex>
        </Flex>

        <Flex innerMargin="md" justify="flex-start">
          <Button disabled={form.isSubmitting} onClick={form.submitForm} size="lg" testName="submit">
            {form.isSubmitting ? (
              <Flex direction="row" innerMargin="sm" align="center" justify="center">
                <LoadingSpinner />
                <Text size="lg">{t("CONSULTANT_PROFILES.INVITATION.FORM.BUTTON_ADD_PROFILE")}</Text>
              </Flex>
            ) : (
              <Text size="lg">{t("CONSULTANT_PROFILES.INVITATION.FORM.BUTTON_ADD_PROFILE")}</Text>
            )}
          </Button>
          <Button variant="secondary" onClick={onCancel} size="lg" testName="cancel">
            {t("CONSULTANT_PROFILES.INVITATION.FORM.BUTTON_CANCEL")}
          </Button>
        </Flex>
      </Flex>
    </FormikForm>
  );
};

interface IInvitationFormikProps extends Omit<IInvitationFormProps, "form"> {
  initialValues?: IInvitationFormValues;
  onSubmit: (values: IInvitationFormValues, actions: FormikHelpers<IInvitationFormValues>) => Promise<void>;
}

export const InvitationFormik: FC<IInvitationFormikProps> = memo(
  ({ initialValues = FORM_INITIAL_VALUES, onSubmit, ...rest }) => (
    <Formik initialValues={initialValues} validationSchema={formValidationSchema} onSubmit={onSubmit}>
      {(form: FormikProps<IInvitationFormValues>) => <InvitationForm form={form} {...rest} />}
    </Formik>
  ),
);
